// React
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { NumericFormat } from "react-number-format";

// MUI
import {
    Box,
    TextField,
    Grid,
    Divider,
    Card,
    CardHeader,
    CardContent,
    Button,
    FormControl,
    InputLabel,
    OutlinedInput,
    InputAdornment,
    Select,
    MenuItem,
    Paper,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";

/** MUI icons */
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const PREFIX = "Other";

const classes = {
    btnRoot: `${PREFIX}-btnRoot`,
    buttonContainer: `${PREFIX}-buttonContainer`,
};

const StyledContainer = styled(Box)(({ theme }) => ({
    [`& .${classes.btnRoot}`]: {
        border: "1px solid #ff5968",
        borderRadius: 40,
        "&:hover": {
            backgroundColor: "#2f3190",
            border: "1px solid #2f3190",
        },
    },
    [`& .${classes.buttonContainer}`]: {
        display: "flex",
        justifyContent: "flex-end",
        padding: 15,
    },
}));

const NumberFormatCustom = (props) => {
    const todos = useSelector((state) => state.todos);

    const { inputRef, value, onChange, ...other } = props;

    return (
        <NumericFormat
            {...other}
            value={value}
            getInputRef={inputRef}
            onValueChange={({ value: v }) => onChange({ target: { value: v } })}
            thousandSeparator={todos.amountFormat === "de-DE" ? "." : ","}
            decimalSeparator={todos.amountFormat === "de-DE" ? "," : "."}
            fixedDecimalScale={true}
            decimalScale={2}
            valueIsNumericString
        />
    );
};

const defaultState = {
    ogFee: 0,
    hours: 1,
    fee: 0,
    discount: 0,
    totalFee: 0,
    package: 4,
    software: "",
    estimatedDate: null,
    deliveryDate: null,
};

const filter = createFilterOptions();

export const Other = (props) => {
    // Properties
    const { setTexto, setalertType, setShow, companyID, setBackDrop, data, reloadProjects, serviceID } = props;

    /** Internationalization i18n */
    const [t] = useTranslation("global");

    /** React redux */
    const todos = useSelector((state) => state.todos);

    /** Component states */
    const [other, setOther] = useState(defaultState);
    const [isError, setIsError] = useState(false);
    const [softwares, setSoftwares] = useState([]);

    /** Component functions */
    useEffect(() => {
        (async () => {
            setOther({
                hours: data.consulting_hours ?? 1,
                ogFee: data.original_fee ?? data.fee,
                fee: (data.original_fee ?? data.fee) * (data.consulting_hours ?? 1),
                discount: data.discount ?? 0,
                totalFee:
                    (data.original_fee ?? data.fee) * (data.consulting_hours ?? 1) * (1 - (data.discount ?? 0) / 100),
                package: data.package ?? 4,
                software: data.version ?? "",
                estimatedDate: data.client_commitment_date
                    ? moment(data.client_commitment_date).utc()
                    : null,
                deliveryDate: data.client_delivery_date ? moment(data.client_delivery_date).utc() : null,
            });

            await fetch(`/getSoftwares`, {
                method: "GET",
                headers: {
                    "Content-type": "application/json; charset=UTF-8",
                    "Access-Control-Allow-Origin": "*",
                },
            })
                .then((res) => res.json())
                .then((data) => {
                    setSoftwares(data.softwares);
                });
        })();
    }, [data]);

    const handleChange = (event) => {
        setOther({
            ...other,
            [event.target.name]: event.target.value,
            fee: event.target.value * other.ogFee,
            totalFee: event.target.value * other.ogFee * (1 - other.discount / 100),
        });
    };

    const handleChangeDiscount = (event) => {
        let discountAux = event.target.value >= 0 && event.target.value <= 100 ? event.target.value : other.discount;

        setOther({
            ...other,
            discount: discountAux,
            totalFee: other.fee * (1 - discountAux / 100),
        });
    };

    const getSoftwares = async () => {
        await fetch(`/getSoftwares`, {
            method: "GET",
            headers: {
                "Content-type": "application/json; charset=UTF-8",
                "Access-Control-Allow-Origin": "*",
            },
        })
            .then((res) => res.json())
            .then((data) => {
                setSoftwares(data.softwares);
            });
    };

    const updateProject = async () => {
        setBackDrop(true);

        if (other.software && other.package && other.estimatedDate) {
            if (!softwares.includes(other.software)) {
                const paramsSoft = {
                    software_name: other.software,
                };

                await fetch(`/addSoftware`, {
                    method: "POST",
                    headers: {
                        "Content-type": "application/json; charset=UTF-8",
                        "Access-Control-Allow-Origin": "*",
                    },
                    body: JSON.stringify(paramsSoft),
                })
                    .then((response) => {
                        if (response.ok) {
                            getSoftwares();
                        }
                    })
                    .catch((error) => {
                        console.log(error);
                    });
            }

            let params = {
                company_id: companyID,
                service_id: serviceID,
                project_id: data.project_id,
                project_company_id: data.project_company_id,
                client_delivery_date: other.deliveryDate ? moment(other.deliveryDate).startOf("day").format() : null,
                client_commitment_date: moment(other.estimatedDate).startOf("day").format(),
                discount: other.discount ? parseFloat(other.discount) : 0,
                fee: other.totalFee,
                package: other.package,
                version: other.software,
                consulting_hours: parseFloat(other.hours),
            };

            await fetch(`/addServicesAndProjects`, {
                method: "POST",
                headers: {
                    "Content-type": "application/json; charset=UTF-8",
                    "Access-Control-Allow-Origin": "*",
                },
                body: JSON.stringify(params),
            })
                .then((response) => {
                    if (response.ok) {
                        setTexto(t("miscellaneous.successfulUpdate"));
                        setalertType("success");
                        setShow(true);
                        reloadProjects(true);
                    } else {
                        setTexto(t("miscellaneous.updatingError"));
                        setalertType("error");
                        setShow(true);
                    }
                })
                .catch((error) => {
                    console.log(error);
                });
        } else {
            setTexto(t("miscellaneous.requiredData"));
            setalertType("warning");
            setShow(true);
            setIsError(true);
        }

        setBackDrop(false);
    };

    const handleChangeEstimatedDate = (date) => {
        if (!date || !other.deliveryDate) {
            setOther({
                ...other,
                estimatedDate: date,
            });
        } else {
            if (date.isAfter(other.deliveryDate.format("YYYY-MM-DD"))) {
                setOther({
                    ...other,
                    estimatedDate: null,
                });

                setTexto(t("reports.meetingDateMess"));
                setalertType("warning");
                setShow(true);
            } else {
                setOther({
                    ...other,
                    estimatedDate: date,
                });
            }
        }
    };

    const handleChangeDeliveryDate = (date) => {
        if (!date || !other.estimatedDate) {
            setOther({
                ...other,
                deliveryDate: date,
            });
        } else {
            if (date.isBefore(other.estimatedDate.format("YYYY-MM-DD"))) {
                setOther({
                    ...other,
                    deliveryDate: null,
                });

                setTexto(t("reports.meetingDateMess"));
                setalertType("warning");
                setShow(true);
            } else {
                setOther({
                    ...other,
                    deliveryDate: date,
                });
            }
        }
    };

    const handleChangePackage = (event) => {
        setOther({
            ...other,
            package: event.target.value,
        });
    };

    const handleChangeSoftwares = (event, value, reason) => {
        if (reason === "clear" || reason === "createOption") {
            setOther({
                ...other,
                software: "",
            });
        } else {
            if (value && value.inputValue) {
                // Create a new value from the user input
                setOther({
                    ...other,
                    software: value.inputValue,
                });
            } else {
                setOther({
                    ...other,
                    software: value.software_name,
                });
            }
        }
    };

    return (
        <StyledContainer>
            <LocalizationProvider
                dateAdapter={AdapterMoment}
                adapterLocale={t("language.locale")}
                localeText={{ clearButtonLabel: t("miscellaneous.clear") }}
            >
                <Card elevation={0}>
                    <CardHeader title="Other setup" />
                    <Divider />
                    <CardContent>
                        <Grid container alignItems="flex-start" spacing={2}>
                            <Grid item xs={12} sm={6} lg={2}>
                                <FormControl fullWidth variant="outlined" disabled>
                                    <InputLabel htmlFor="outlined-adornment-ogFee">
                                        {t("services.hourlyRate")}
                                    </InputLabel>
                                    <OutlinedInput
                                        id="outlined-adornment-ogFee"
                                        value={other.ogFee}
                                        inputComponent={NumberFormatCustom}
                                        startAdornment={
                                            <InputAdornment disableTypography position="start">
                                                $
                                            </InputAdornment>
                                        }
                                        label={t("services.hourlyRate")}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} lg={1}>
                                <TextField
                                    fullWidth
                                    label={t("miscellaneous.hours")}
                                    name="hours"
                                    onChange={handleChange}
                                    value={other.hours}
                                    variant="outlined"
                                    type="number"
                                    inputProps={{
                                        min: 1,
                                        step: 0.5,
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FormControl fullWidth variant="outlined" disabled>
                                    <InputLabel htmlFor="outlined-adornment-fee">{t("services.projectFee")}</InputLabel>
                                    <OutlinedInput
                                        id="outlined-adornment-fee"
                                        value={other.fee}
                                        inputComponent={NumberFormatCustom}
                                        startAdornment={
                                            <InputAdornment disableTypography position="start">
                                                $
                                            </InputAdornment>
                                        }
                                        label={t("services.projectFee")}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <TextField
                                    fullWidth
                                    label={t("miscellaneous.discount")}
                                    value={other.discount}
                                    name="discount"
                                    onChange={handleChangeDiscount}
                                    onBlur={() =>
                                        setOther({
                                            ...other,
                                            discount: parseFloat(other.discount === "" ? 0 : other.discount).toFixed(2),
                                        })
                                    }
                                    variant="outlined"
                                    type="number"
                                    InputProps={{
                                        inputProps: {
                                            min: 0,
                                            max: 100,
                                        },
                                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                    }}
                                    sx={{
                                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                                            display: "none",
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FormControl fullWidth variant="outlined" disabled>
                                    <InputLabel htmlFor="outlined-adornment-totalFee">
                                        {t("proposals.feeTotal")}
                                    </InputLabel>
                                    <OutlinedInput
                                        id="outlined-adornment-totalFee"
                                        value={other.totalFee}
                                        inputComponent={NumberFormatCustom}
                                        startAdornment={
                                            <InputAdornment disableTypography position="start">
                                                $
                                            </InputAdornment>
                                        }
                                        label={t("proposals.feeTotal")}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
                <Card elevation={0}>
                    <CardHeader title={t("services.serviceResponsibility")} sx={{ color: "#ff5968" }} />
                    <Divider />
                    <CardContent>
                        <Grid container alignItems="center" spacing={2}>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FormControl fullWidth required error={isError && other.package === ""}>
                                    <InputLabel id="ver-simple-select-label">{t("services.package")}</InputLabel>
                                    <Select
                                        name="package"
                                        labelId="ver-simple-select-label"
                                        IconComponent={KeyboardArrowDownIcon}
                                        value={other.package}
                                        label={t("services.package")}
                                        onChange={handleChangePackage}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    mt: 0.7,
                                                    borderRadius: 2,
                                                    boxShadow: "10px 10px 15px 0px rgba(3,24,81,0.15)",
                                                },
                                            },
                                        }}
                                    >
                                        <MenuItem value={4}>{t("currentView.personal")}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <Autocomplete
                                    id="software-autocomplete"
                                    value={other.software}
                                    onChange={(event, value, reason) => handleChangeSoftwares(event, value, reason)}
                                    filterOptions={(options, params) => {
                                        const filtered = filter(options, params);

                                        const { inputValue } = params;
                                        // Suggest the creation of a new value
                                        const isExisting = options.some(
                                            (option) => inputValue === option.software_name
                                        );
                                        if (inputValue !== "" && !isExisting) {
                                            filtered.push({
                                                inputValue,
                                                software_name: `${t("services.addSoft")} "${inputValue}"`,
                                            });
                                        }

                                        return filtered;
                                    }}
                                    selectOnFocus
                                    clearOnBlur
                                    handleHomeEndKeys
                                    options={softwares}
                                    getOptionLabel={(option) => {
                                        // Value selected with enter, right from the input
                                        if (typeof option === "string") {
                                            return option;
                                        }
                                        // Add "xxx" option created dynamically
                                        if (option.inputValue) {
                                            return option.inputValue;
                                        }
                                        // Regular option
                                        return option.software_name;
                                    }}
                                    renderOption={(props, option) => <li {...props}>{option.software_name}</li>}
                                    renderInput={(params) => (
                                        <TextField
                                            required
                                            {...params}
                                            error={isError && other.software === ""}
                                            label="Software"
                                        />
                                    )}
                                    popupIcon={<KeyboardArrowDownIcon color="primary" />}
                                    PaperComponent={({ children }) => (
                                        <Paper
                                            sx={{
                                                mt: 0.7,
                                                borderRadius: 2,
                                                boxShadow: "10px 10px 15px 0px rgba(3,24,81,0.15)",
                                            }}
                                        >
                                            {children}
                                        </Paper>
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    value={other.estimatedDate}
                                    onChange={(newValue) => handleChangeEstimatedDate(newValue)}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            required
                                            fullWidth
                                            {...params}
                                            error={isError && !other.estimatedDate}
                                        />
                                    )}
                                    inputProps={{ readOnly: true }}
                                    label={t("services.estimDeliveryDate")}
                                    views={["year", "month", "day"]}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    value={other.deliveryDate}
                                    onChange={(newValue) => handleChangeDeliveryDate(newValue)}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => <TextField fullWidth {...params} />}
                                    inputProps={{ readOnly: true }}
                                    label={t("services.deliveryDate")}
                                    views={["year", "month", "day"]}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                    <Divider />
                    <Box className={classes.buttonContainer}>
                        <Button
                            color="secondary"
                            variant="contained"
                            disableElevation
                            classes={{ root: classes.btnRoot }}
                            onClick={updateProject}
                        >
                            {t("miscellaneous.save")}
                        </Button>
                    </Box>
                </Card>
            </LocalizationProvider>
        </StyledContainer>
    );
};
